import React, { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import { sendCode, verifyCode, register } from './authApi.jsx';
import './authModal.scss';

// ورود / ثبت‌نام: شماره ← کدِ تأیید ← (فقط کاربرِ جدید) نام، نام خانوادگی، نوعِ کاربری

// سوییچِ دو‌طرفه: گزینه‌ی اول (راست) متقاضی، گزینه‌ی دوم (چپ) مشاور
const ROLES = [
  { id: 'client', label: 'متقاضی' },
  { id: 'consultant', label: 'مشاور املاک' },
];

// تبدیل ارقام فارسی/عربی به انگلیسی تا اعتبارسنجی درست کار کنه
function normalizeDigits(value) {
  const persian = '۰۱۲۳۴۵۶۷۸۹';
  const arabic = '٠١٢٣٤٥٦٧٨٩';
  return value.replace(/[۰-۹٠-٩]/g, (ch) => {
    const pIndex = persian.indexOf(ch);
    if (pIndex !== -1) return String(pIndex);
    const aIndex = arabic.indexOf(ch);
    if (aIndex !== -1) return String(aIndex);
    return ch;
  });
}

const fa = (n) => Number(n).toLocaleString('fa-IR');
const faDigits = (s) => String(s).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[d]);

function AuthModal({ onClose, onSuccess }) {
  const [step, setStep] = useState('phone'); // 'phone' | 'otp' | 'profile'
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [codeLength, setCodeLength] = useState(4);
  const [testCode, setTestCode] = useState('');
  const [resendAt, setResendAt] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [signupToken, setSignupToken] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState('client');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const otpRef = useRef(null);

  // شمارشِ معکوسِ «ارسالِ دوباره»
  useEffect(() => {
    if (step !== 'otp' || resendAt <= Date.now()) return undefined;
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, [step, resendAt]);

  useEffect(() => {
    if (step === 'otp' && otpRef.current) otpRef.current.focus();
  }, [step]);

  const isPhoneValid = /^09\d{9}$/.test(phone);
  const resendIn = Math.max(0, Math.ceil((resendAt - now) / 1000));

  const requestCode = async () => {
    setBusy(true);
    setError('');
    try {
      const r = await sendCode(phone);
      setCodeLength(r.codeLength || 4);
      setTestCode(r.testMode && r.testCode ? r.testCode : '');
      setResendAt(Date.now() + (r.resendIn || 60) * 1000);
      setNow(Date.now());
      setOtp('');
      setStep('otp');
    } catch (err) {
      // کد قبلاً فرستاده شده و هنوز معتبر است → برو سراغِ وارد کردنش
      if (err.code === 'wait' && step === 'phone') {
        setResendAt(Date.now() + (err.data?.resendIn || 60) * 1000);
        setNow(Date.now());
        setStep('otp');
      } else {
        setError(err.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleSendCode = (e) => {
    e.preventDefault();
    if (!isPhoneValid) {
      setError('شماره موبایل را درست وارد کنید (مثل ۰۹۱۲۱۲۳۴۵۶۷)');
      return;
    }
    requestCode();
  };

  const submitOtp = async (code) => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const r = await verifyCode(phone, code);
      if (r.needsProfile) {
        setSignupToken(r.signupToken);
        setStep('profile');
      } else {
        onSuccess?.(r.user);
      }
    } catch (err) {
      setError(err.message);
      setOtp('');
    } finally {
      setBusy(false);
    }
  };

  const handleOtpChange = (e) => {
    const v = normalizeDigits(e.target.value).replace(/\D/g, '').slice(0, codeLength);
    setOtp(v);
    if (v.length === codeLength) submitOtp(v); // با کاملِ شدنِ کد خودش تأیید می‌کند
  };

  const handleVerify = (e) => {
    e.preventDefault();
    if (otp.length !== codeLength) {
      setError(`کد تایید باید ${fa(codeLength)} رقم باشد`);
      return;
    }
    submitOtp(otp);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) {
      setError('نام و نام خانوادگی را وارد کنید');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const r = await register(signupToken, firstName, lastName, role);
      onSuccess?.(r.user);
    } catch (err) {
      if (err.code === 'signup-expired') {
        setStep('phone');
        setOtp('');
      }
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const titles = { phone: 'ورود / ثبت‌نام', otp: 'کد تایید', profile: 'تکمیل ثبت‌نام' };

  return (
    <div className="auth-modal">
      <div className="auth-modal__backdrop" onClick={busy ? undefined : onClose} />
      <div className="auth-modal__sheet">
        <div className="auth-modal__header">
          <h2>{titles[step]}</h2>
          <button type="button" className="auth-modal__close" onClick={onClose} aria-label="بستن" disabled={busy}>
            <X size={20} strokeWidth={1.8} />
          </button>
        </div>

        {step === 'phone' && (
          <form className="auth-modal__form" onSubmit={handleSendCode}>
            <p className="auth-modal__hint">شماره موبایل خود را وارد کنید. اگر حساب ندارید، بعد از تایید ساخته می‌شود.</p>
            <label className="auth-modal__field">
              <span>شماره موبایل</span>
              <input
                type="tel"
                inputMode="numeric"
                autoComplete="tel"
                placeholder="09xxxxxxxxx"
                value={phone}
                onChange={(e) => setPhone(normalizeDigits(e.target.value).replace(/[^\d]/g, ''))}
                maxLength={11}
                className="auth-modal__phone-input"
                disabled={busy}
                autoFocus
              />
            </label>

            {error && <p className="auth-modal__error">{error}</p>}

            <button type="submit" className="auth-modal__submit" disabled={busy || !isPhoneValid}>
              {busy ? 'در حال ارسال…' : 'دریافت کد تایید'}
            </button>
          </form>
        )}

        {step === 'otp' && (
          <form className="auth-modal__form" onSubmit={handleVerify}>
            <p className="auth-modal__hint">کد تایید به شماره {faDigits(phone)} پیامک شد</p>
            {testCode && (
              <p className="auth-modal__test">نسخه‌ی آزمایشی: پیامک فرستاده نمی‌شود — کد {faDigits(testCode)} را وارد کنید.</p>
            )}

            <label className="auth-modal__field">
              <span>کد تایید</span>
              <input
                ref={otpRef}
                type="tel"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder={Array.from({ length: codeLength }, () => '-').join(' ')}
                value={otp}
                onChange={handleOtpChange}
                maxLength={codeLength}
                className="auth-modal__otp-input"
                disabled={busy}
              />
            </label>

            {error && <p className="auth-modal__error">{error}</p>}

            <button type="submit" className="auth-modal__submit" disabled={busy || otp.length !== codeLength}>
              {busy ? 'در حال بررسی…' : 'تایید'}
            </button>

            <div className="auth-modal__row">
              <button
                type="button"
                className="auth-modal__back"
                onClick={() => {
                  setStep('phone');
                  setError('');
                }}
                disabled={busy}
              >
                تغییر شماره
              </button>
              <button type="button" className="auth-modal__back" onClick={requestCode} disabled={busy || resendIn > 0}>
                {resendIn > 0 ? `ارسال دوباره تا ${fa(resendIn)} ثانیه` : 'ارسال دوباره کد'}
              </button>
            </div>
          </form>
        )}

        {step === 'profile' && (
          <form className="auth-modal__form" onSubmit={handleRegister}>
            <p className="auth-modal__hint">شماره شما تایید شد. برای ساخت حساب این اطلاعات را کامل کنید.</p>

            <label className="auth-modal__field">
              <span>نام</span>
              <input
                type="text"
                autoComplete="given-name"
                placeholder="نام"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                maxLength={40}
                disabled={busy}
              />
            </label>

            <label className="auth-modal__field">
              <span>نام خانوادگی</span>
              <input
                type="text"
                autoComplete="family-name"
                placeholder="نام خانوادگی"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                maxLength={40}
                disabled={busy}
              />
            </label>

            <div className="auth-modal__field">
              <span>نوع کاربری</span>
              <div
                className={`auth-modal__switch ${role === 'consultant' ? 'is-consultant' : ''}`}
                role="radiogroup"
                aria-label="نوع کاربری"
              >
                <span
                  className={`auth-modal__switch-thumb ${role === 'consultant' ? 'is-consultant' : ''}`}
                  aria-hidden="true"
                />
                {ROLES.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    role="radio"
                    aria-checked={role === r.id}
                    className={`auth-modal__switch-option ${role === r.id ? 'is-selected' : ''}`}
                    onClick={() => setRole(r.id)}
                    disabled={busy}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>

            {error && <p className="auth-modal__error">{error}</p>}

            <button type="submit" className="auth-modal__submit" disabled={busy}>
              {busy ? 'در حال ثبت…' : 'ساخت حساب و ورود'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default AuthModal;
